import { WidgetConfiguration } from "@sgwt-widget/core";
import { ISCUser } from "../salescredit/salescredit-role-types";
import { cprScApiRequest } from "../sc-cheeky-api";
import { getJsonHandler } from "../utils/api";
import { IHeaders } from "../utils/types";
import {
  GenericExportBackgroundResponse,
  handleBlob,
  handleNonOkExportResponse,
} from "./export-helper";
import {
  GenericAttributeValues,
  IGenericAttributeRequest,
  IGenericAttributeValues,
} from "./generic-attribute-types";
import {
  isForbiddenError,
  isInternalServerError,
  isNotFoundError,
  isPreconditionFailedError,
  isUnauthorizedError,
  timeout,
} from "./generic-helper";
import {
  GenericAggregateResponse,
  IAbacusRequest,
  IAbacusResponse,
  IGenericAggregateResponse,
  IGenericCsvRequest,
  IGenericRequest,
} from "./generic-types";

async function MakeGenericApiCallGeneric(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericRequest,
  configuration: WidgetConfiguration,
  rootURI: string,
  correlationId: string,
  isPrivate: boolean
): Promise<IGenericAggregateResponse> {
  try {
    const apiResponse = await cprScApiRequest({
      baseUri: rootURI + indicatorId,
      configuration,
      correlationId,
      headers,
      isPrivate,
      method: "GET",
    })(criteria);
    return GetGenericAggregateResponse(apiResponse);
  } catch (error) {
    const response = new GenericAggregateResponse();
    response.clientRequestError = true;
    return response;
  }
}

export async function MakeGenericApiCall(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAggregateResponse> {
  return MakeGenericApiCallGeneric(
    headers,
    indicatorId,
    criteria,
    configuration,
    "aggregates/",
    correlationId,
    false
  );
}

// tslint:disable-next-line: no-identical-functions
export async function MakeAirRetentionRateCall(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAggregateResponse> {
  return MakeGenericApiCallGeneric(
    headers,
    indicatorId,
    criteria,
    configuration,
    "AirRetentionRateAndSCPerimeter/",
    correlationId,
    true
  );
}

// tslint:disable-next-line: no-identical-functions
export async function MakeBookValueAdjustBySalesCall(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAggregateResponse> {
  return MakeGenericApiCallGeneric(
    headers,
    indicatorId,
    criteria,
    configuration,
    "AirRetentionRateAdjustedBySales/",
    correlationId,
    true
  );
}

export async function MakeBudgetApiCall(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAggregateResponse> {
  try {
    const apiResponse = await cprScApiRequest({
      baseUri: `budgetAggregates/${indicatorId}`,
      configuration,
      correlationId,
      headers,
      isPrivate: true,
      method: "GET",
    })(criteria);
    try {
      if (!apiResponse.ok) {
        const response = new GenericAggregateResponse();
        if (isForbiddenError(apiResponse) || isUnauthorizedError(apiResponse)) {
          response.noAccessRights = true;
          return response;
        }

        if (
          isNotFoundError(apiResponse) ||
          isInternalServerError(apiResponse)
        ) {
          response.serverError = true;
          return response;
        }
        if (isPreconditionFailedError(apiResponse)) {
          const statusText = await apiResponse.json();
          if (
            statusText === "Invalid role for budget" ||
            statusText === "Invalid filters for budget"
          ) {
            response.noBudgetAccess = true;
          }
          return response;
        }
        return response;
      }
      const dataResponse = await apiResponse.json();
      return getMapping(dataResponse);
    } catch (error) {
      const response = new GenericAggregateResponse();
      response.serverError = true;
      return response;
    }
  } catch (error) {
    const response = new GenericAggregateResponse();
    response.clientRequestError = true;
    return response;
  }
}

export async function MakeGenericBucketApiCall(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAggregateResponse> {
  const apiResponse = await cprScApiRequest({
    baseUri: `buckets/${indicatorId}`,
    configuration,
    correlationId,
    headers,
    isPrivate: false,
    method: "GET",
  })(criteria);

  return GetGenericAggregateResponse(apiResponse);
}

async function GetGenericAggregateResponse(
  apiResponse: Response
): Promise<IGenericAggregateResponse> {
  try {
    if (!apiResponse.ok) {
      const statusText = (await apiResponse.json()).detail;
      return handleApiResponse(apiResponse.status, statusText);
    }
    const dataResponse = await apiResponse.json();
    return getMapping(dataResponse);
  } catch (error) {
    return handleApiResponse(
      apiResponse.status,
      (await apiResponse.json()).detail
    );
  }
}

export async function handleApiResponse(status: number, statusText: string) {
  const response = new GenericAggregateResponse();
  switch (status) {
    case 401:
      if (
        statusText === "Authentication failed" ||
        statusText === "Unauthorized"
      ) {
        response.noAccessRights = true;
      }
      break;
    case 404:
    case 500:
      response.serverError = true;
      break;
    case 504:
      response.clientRequestError = true;
      break;
    case 403:
      if (statusText === "You are not authorized to see this client") {
        response.noClientMapping = true;
      } else {
        response.noAccessRights = true;
      }
      response.statusText = statusText;
      break;
  }
  return response;
}

export async function MakeBackgroundExportDataApiCall(
  headers: IHeaders,
  indicatorId: string,
  criteria: IGenericCsvRequest,
  filename: string,
  configuration: WidgetConfiguration,
  correlationId: string
) {
  const apiResponse = await cprScApiRequest({
    baseUri: `exportdata/background/start/${indicatorId}`,
    configuration,
    correlationId,
    headers,
    isPrivate: true,
    method: "POST",
  })(criteria);
  try {
    let fileIdResponse = new GenericExportBackgroundResponse();
    if (!apiResponse.ok) {
      fileIdResponse = await handleNonOkExportResponse(
        apiResponse,
        fileIdResponse
      );
      return fileIdResponse;
    }
    fileIdResponse = await apiResponse.json();
    await timeout(3000);
    const apiStatusResponse = await backgroundGetFile(fileIdResponse.fileId);
    if (!apiStatusResponse.ok) {
      fileIdResponse = await handleNonOkExportResponse(
        apiStatusResponse,
        fileIdResponse
      );
      return fileIdResponse;
    }
    const blobData = await handleBlob(apiStatusResponse, filename);
    fileIdResponse.blobData = blobData;
    return fileIdResponse;
  } catch (error) {
    const response = new GenericAggregateResponse();
    response.serverError = true;
    return response;
  }

  async function backgroundGetFile(fileId: string): Promise<Response> {
    const apiStatusResponse = await cprScApiRequest({
      baseUri: `exportdata/background/getfile/${fileId}`,
      configuration,
      correlationId,
      headers,
      isPrivate: true,
      method: "GET",
    })({});

    if (apiStatusResponse.status === 206) {
      await timeout(10000);
      return backgroundGetFile(fileId);
    }
    if (!apiStatusResponse.ok) {
      return apiStatusResponse;
    }

    return apiStatusResponse;
  }
}

function getMapping(data: any) {
  return data as GenericAggregateResponse;
}

export async function MakeGenericAttributeApiCall(
  headers: IHeaders,
  indicatorId: string,
  attribute: string,
  requestDetails: IGenericAttributeRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAttributeValues> {
  const apiResponse = await cprScApiRequest({
    baseUri: `attributes/${indicatorId}/${attribute}/values`,
    configuration,
    correlationId,
    headers,
    isPrivate: false,
    method: "GET",
  })(requestDetails);

  return getJsonHandler<GenericAttributeValues>()(apiResponse);
}

export async function MakeAbacusFilterApiCall(
  headers: IHeaders,
  requestDetails: any,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IGenericAttributeValues> {
  const apiResponse = await cprScApiRequest({
    baseUri: `GetAbacusFilterValues`,
    configuration,
    correlationId,
    headers,
    isPrivate: true,
    method: "GET",
  })(requestDetails);

  return getJsonHandler<GenericAttributeValues>()(apiResponse);
}

export async function MakeSalesCreditRoleProfile(
  headers: IHeaders,
  configuration: WidgetConfiguration,
  indicator = "sc",
  correlationId: string
): Promise<ISCUser> {
  const userRoleFromBus = configuration.bus.dangerouslyGetCurrentValue<ISCUser>(
    "cprofit.user-role"
  );

  if (userRoleFromBus !== undefined) {
    return userRoleFromBus;
  }
  const apiResponse = await cprScApiRequest({
    baseUri: `userrole/${indicator}`,
    configuration,
    correlationId,
    headers,
    isPrivate: true,
    method: "GET",
  })({});
  const dataResponse = await apiResponse.json();
  const userRole: ISCUser = getUserRoleProfile(dataResponse);

  if (userRole && userRole.TechnicalRole === "unit-tests") {
    return userRole;
  }
  configuration.bus.publish("cprofit.user-role", userRole);

  return userRole;
}

function getUserRoleProfile(data: any): ISCUser {
  const role = data.role ? data.role : undefined;
  const profile = data.profile ? data.profile : undefined;
  return {
    Profile: profile,
    Role: role,
    TechnicalRole: data.technicalRole,
    StatusText: data.detail,
  };
}

export async function MakeAbacusApiCall(
  headers: IHeaders,
  criteria: IAbacusRequest,
  configuration: WidgetConfiguration,
  correlationId: string
): Promise<IAbacusResponse> {
  const apiResponse = await cprScApiRequest({
    baseUri: "GetAbacusclientscarceSummary",
    configuration,
    correlationId,
    headers,
    isPrivate: true,
    method: "POST",
  })(criteria);
  try {
    if (!apiResponse.ok) {
      const response: IAbacusResponse = {
        data: [],
        dimension: { key: "", value: "" },
        measureInfo: [],
      };
      switch (apiResponse.status) {
        case 401:
          if (apiResponse.statusText === "Forbidden") {
            response.noAccessRights = true;
          }
          break;
        case 404:
        case 500:
          response.serverError = true;
          break;
        case 403:
          const statusText = (await apiResponse.json()).detail;
          if (statusText === "You are not authorized to see this client") {
            response.noClientMapping = true;
          } else {
            response.noAccessRights = true;
          }
          response.statusText = statusText;
          break;
      }
      return response;
    }
    return await apiResponse.json();
  } catch (error) {
    const response: IAbacusResponse = {
      data: [],
      dimension: { key: "", value: "" },
      measureInfo: [],
    };
    response.serverError = true;
    return response;
  }
}
